// BOM sections in an item's side panel: its own BOM (sell items through their Shopify product,
// sub-assemblies directly) with what this variant costs, and where it's used, through sub-assemblies up
// to the products that finally use it (or, when none in use does, the newer drafts that have it).
import { ArrowRight, Plus, Replace } from "lucide-react";
import { useState } from "react";
import {
  type BomIndex,
  type BomRow,
  componentsOf,
  currentRevision,
  familyKey,
  ownerLabel,
  resolve,
  revisionState,
  type Usage,
  whereUsed,
} from "../../../shared/boms.ts";
import type { Item } from "../../../shared/db.gen.ts";
import { pendingRevisions } from "../../../shared/project-reach.ts";
import { partProjects, reachOf } from "../../../shared/projects.ts";
import { bomLink, cost2, createBom, currentBomOf, familyOfItem, marginTone, pct, STATE_TONE, useBomIndex } from "../lib/boms.ts";
import { qty } from "../lib/format.ts";
import { can } from "../lib/mutate.ts";
import { useProjectIndex } from "../lib/projects.ts";
import { Link, useSearch } from "../lib/router.tsx";
import { Badge, Button } from "../ui/controls.tsx";
import { Section } from "../ui/RecordPanel.tsx";
import { ReplacePartDialog } from "./BomReplace.tsx";

/** `hideEmpty`: an item bought finished shows no "No BOM yet" (its where-used still shows). */
export function BomItemSection({ item, hideEmpty = false }: { item: Item; hideEmpty?: boolean }) {
  const ix = useBomIndex();
  const key = familyOfItem(item);
  const own = currentBomOf(ix, key);
  const used = whereUsed(ix, item.id);
  // When no variant uses it: the revisions that have it on a line anyway, each family's one in use (a line no variant
  // picks) and its newer drafts and scheduled ones.
  const onLines = used.length
    ? []
    : [...ix.families.keys()]
        .flatMap((k) => [currentRevision(ix, k), ...pendingRevisions(ix, k)])
        .filter((b): b is BomRow => !!b && componentsOf(ix, b.id).has(item.id));
  // Manufacturing projects --- why nothing is listed: a kit nothing uses is in its project through its own BOM's
  // Project; one whose BOM names none is in no project until it's set (New part from ?project= starts its BOM there).
  const px = useProjectIndex();
  const tops = [...reachOf(px).byProject].filter(([, r]) => r.parts.get(item.id)?.top).map(([id]) => px.projectById.get(id)?.name ?? id);
  const noUseText =
    item.source !== "local"
      ? "Not in any BOM."
      : tops.length
        ? `Nothing uses it: it's in ${tops.join(" and ")} through its own BOM's Project.`
        : item.kind === "subassembly" && own
          ? "Nothing uses it and its BOM names no project: set Project on its BOM to put it in one."
          : "Not on a BOM yet.";
  const wanted = useSearch().get("project");
  const startIn = item.kind === "subassembly" && wanted && px.projectById.has(wanted) && !partProjects(px).get(item.id)?.onBom ? wanted : null;
  const [replacing, setReplacing] = useState(false);
  const canHaveBom = item.kind === "subassembly" || (item.kind === "sell" && !!item.shopify_product_id);

  return (
    <>
      {canHaveBom && (own || !hideEmpty) && (
        <Section title="BOM">
          <div className="px-2 pb-2">
            {own ? (
              <OwnBom ix={ix} item={item} bomId={own.id} />
            ) : (
              <div className="flex items-center gap-2 px-2">
                <span className="text-muted">No BOM yet.</span>
                {can("bom.create") && (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Plus className="size-3.5" />}
                    title={
                      startIn ? `Its BOM's Projects start with ${px.projectById.get(startIn)?.name}: it's in that project while nothing uses it` : undefined
                    }
                    onClick={() =>
                      createBom(
                        ix,
                        item.kind === "subassembly"
                          ? { item_id: item.id, project_ids: startIn ? [startIn] : [] }
                          : { shopify_product_id: item.shopify_product_id as string },
                      )
                    }
                  >
                    {item.kind === "subassembly" ? "Start its BOM" : "Start the product's BOM"}
                  </Button>
                )}
              </div>
            )}
          </div>
        </Section>
      )}
      {(used.length > 0 || item.kind !== "sell") && (
        <div id="sec-used-in">
          <Section title="Used in" count={used.length || undefined}>
            <div className="px-2 pb-2">
              {used.length ? (
                used.map((u) => <UsageRow key={u.bom.id} ix={ix} usage={u} depth={0} />)
              ) : onLines.length ? (
                // Manufacturing projects --- only a newer draft uses it (it shows in that project as "· draft" until
                // released), or a line has it that no variant picks (an option or an override nothing resolves to)
                onLines.map((b) => (
                  <Link key={b.id} to={bomLink(b.id)} className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-hover">
                    <span className="min-w-0 flex-1 truncate">
                      {ownerLabel(ix, b)} <span className="text-muted">Rev {b.revision}</span>
                      {currentRevision(ix, familyKey(b))?.id === b.id && <span className="text-faint"> · on a line no variant picks</span>}
                    </span>
                    <Badge tone={STATE_TONE[revisionState(ix, b)]}>{revisionState(ix, b)}</Badge>
                  </Link>
                ))
              ) : (
                <div className="px-2 text-muted">{noUseText}</div>
              )}
              {used.length > 0 && can("bom.replace_part") && (
                <Button size="sm" variant="ghost" icon={<Replace className="size-3.5" />} onClick={() => setReplacing(true)} className="mt-1">
                  Replace it in these BOMs…
                </Button>
              )}
            </div>
          </Section>
        </div>
      )}
      {replacing && <ReplacePartDialog fromId={item.id} onClose={() => setReplacing(false)} />}
    </>
  );
}

function OwnBom({ ix, item, bomId }: { ix: BomIndex; item: Item; bomId: string }) {
  const bom = ix.bomById.get(bomId);
  if (!bom) return null;
  const state = revisionState(ix, bom);
  const lines = ix.linesByBom.get(bomId)?.length ?? 0;
  // This variant's (or this sub-assembly's) own roll-up.
  const r = resolve(ix, bom, ix.itemById.get(item.id));
  const price = item.price == null ? null : Number(item.price);
  const margin = price && r.cost.complete ? (price - r.cost.total) / price : null;
  return (
    <Link to={bomLink(bomId, item.kind === "sell" ? item.id : undefined)} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-hover">
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        Rev {bom.revision} <Badge tone={STATE_TONE[state]}>{state}</Badge>
        <span className="text-muted">· {lines} lines</span>
        {bom.item_id && (bom.phantom || !bom.make_vendor_id) && <Badge>built in the same order</Badge>}
      </span>
      <span className="font-mono text-[12px]">
        {cost2(r.cost.total)}
        {!r.cost.complete && (
          <span className="text-warn" title={`${r.cost.missing} without a cost`}>
            +
          </span>
        )}
      </span>
      {margin != null && <span className={`font-mono text-[12px] ${marginTone(margin)}`}>{pct(margin)}</span>}
      <ArrowRight className="size-3.5 text-faint" />
    </Link>
  );
}

function UsageRow({ ix, usage, depth }: { ix: BomIndex; usage: Usage; depth: number }) {
  const state = revisionState(ix, usage.bom);
  const each = usage.qty ? (usage.qty.min === usage.qty.max ? qty(usage.qty.min) : `${qty(usage.qty.min)}–${qty(usage.qty.max)}`) : "";
  return (
    <>
      <Link to={bomLink(usage.bom.id)} className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-hover" style={{ paddingLeft: 8 + depth * 18 }}>
        {depth > 0 && <span className="text-faint">↳</span>}
        <span className="min-w-0 flex-1 truncate">{usage.label}</span>
        {state !== "active" && <Badge tone={STATE_TONE[state]}>{state}</Badge>}
        {usage.of > 1 && (
          <span className="shrink-0 text-[12px] text-muted">
            {usage.variants} of {usage.of} variants
          </span>
        )}
        <span className="w-12 shrink-0 text-right font-mono text-[12px] text-muted">× {each}</span>
      </Link>
      {usage.parents.map((p) => (
        <UsageRow key={`${usage.bom.id}>${p.bom.id}`} ix={ix} usage={p} depth={depth + 1} />
      ))}
    </>
  );
}
